import React, { useState, useEffect } from "react";
import {
  Table,
  Typography,
  Spin,
  notification,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Popconfirm,
  Row,
  Col,
  Card,
  Statistic,
  Space,
  Divider,
  Tag,
} from "antd";
import { 
  DollarOutlined, 
  FileTextOutlined, 
  ShoppingOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';
import { saveAs } from "file-saver";
import Papa from "papaparse";
import moment from "moment";
import { API_ENDPOINTS } from '../../../utils/constants';

const logoUrl = "/logo.png";    // Path from the public folder
const qrCodeUrl = "/gpay.jpeg"; // Payment QR code

const { Title, Text } = Typography;

const Home = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState(0);
  const [totalStockUsed, setTotalStockUsed] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [form] = Form.useForm();
  const [productOptions, setProductOptions] = useState([]);
  const [totalAdvance, setTotalAdvance] = useState(0);
  const [totalRemaining, setTotalRemaining] = useState(0);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [remainingAmount, setRemainingAmount] = useState(0);

  useEffect(() => {
    fetchInvoices();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (editingInvoice) {
      const total = parseFloat(editingInvoice.total_amount || 0);
      const advance = parseFloat(editingInvoice.advance || 0);
      setRemainingAmount(total - advance);
    }
  }, [editingInvoice]);

  useEffect(() => {
  const filtered = invoices
    .filter((invoice) =>
      invoice.customer_name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    // Sort by date - newest first
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  setFilteredInvoices(filtered);

  const advance = filtered.reduce((sum, invoice) => {
    const advanceAmount = parseFloat(invoice.advance || 0);
    return sum + advanceAmount;
  }, 0);

  const remaining = filtered.reduce((sum, invoice) => {
    const paidAmount = parseFloat(invoice.paid_amount || 0);
    return sum + paidAmount;
  }, 0);

  const totalAmount = filtered.reduce((sum, invoice) => {
    return sum + parseFloat(invoice.total_amount || 0);
  }, 0);

  setTotalAdvance(advance);
  setTotalRemaining(remaining);
  setTotalInvoiceAmount(totalAmount);
}, [searchQuery, invoices]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.GET_PRODUCT);
      const result = await response.json();
      if (result.success) {
        setProductOptions(result.data);
      } else {
        notification.error({ message: "Error", description: result.message });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch products",
      });
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.GET_INVOICE);
      const result = await response.json();

      if (result.success) {
        const data = result.data;
        setInvoices(data);

        const invoiceAmount = data.reduce((sum, invoice) => {
          const totalAmount = parseFloat(invoice.total_amount || 0);
          const discount = parseFloat(invoice.discount || 0);
          return sum + (totalAmount - totalAmount * (discount / 100));
        }, 0);

        const stockUsed = data.reduce((sum, invoice) => {
          return (
            sum +
            (invoice.items
              ? invoice.items.reduce(
                  (productSum, product) => productSum + (product.quantity || 0),
                  0
                )
              : 0)
          );
        }, 0);

        setTotalInvoiceAmount(invoiceAmount);
        setTotalStockUsed(stockUsed);
      } else {
        notification.error({ message: "Error", description: result.message });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch invoices",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceChange = (value) => {
    const total = parseFloat(form.getFieldValue('total_amount') || 0);
    const newRemaining = total - (value || 0);
    setRemainingAmount(newRemaining);
    form.setFieldsValue({ paid_amount: newRemaining });
  };

  const handleTotalAmountChange = (value) => {
    const advance = parseFloat(form.getFieldValue('advance') || 0);
    const newRemaining = (value || 0) - advance;
    setRemainingAmount(newRemaining);
    form.setFieldsValue({ paid_amount: newRemaining });
  };

  // NEW FUNCTION: Send WhatsApp with total remaining to customer
  const sendTotalRemainingReminder = async (customer) => {
    try {
      // Fetch total remaining balance for this customer
      const response = await fetch(
        `${API_ENDPOINTS.GET_REMAINING_AMOUNT}?customer_name=${encodeURIComponent(customer.customer_name)}`
      );
      const data = await response.json();
      const totalRemaining = data?.total_paid_amount || 0;

      Modal.confirm({
        title: 'Send WhatsApp Reminder',
        content: `Are you sure you want to send the total pending amount reminder to ${customer.customer_name}?`,
        okText: 'Send',
        cancelText: 'Cancel',
        onOk: () => {
          // Format number, avoids double 91 prefix
          let phoneNumber = customer.phone_number ? customer.phone_number.replace(/\D/g, '') : '';
          if (phoneNumber.startsWith('91')) phoneNumber = phoneNumber.slice(2);
          phoneNumber = `91${phoneNumber || '9825514255'}`; // default fallback

          const message = `Dear ${customer.customer_name},
          
This is a friendly reminder of your current total pending amount with Yogi Graphics.

Total Outstanding Balance: ₹${parseFloat(totalRemaining).toFixed(2)}

Please clear the dues at your earliest convenience.

Thank you for your business!
Yogi Graphics`;

          const encodedMessage = encodeURIComponent(message);
          window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
          notification.success({
            message: 'Reminder Sent',
            description: `WhatsApp reminder sent to ${customer.customer_name}`,
          });
        }
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch total remaining amount for this customer',
      });
    }
  };

  // Existing WhatsApp reminder for single invoice (if you want to keep it)
  const sendWhatsAppReminder = (invoice) => {
    Modal.confirm({
      title: 'Send WhatsApp Reminder',
      content: `Are you sure you want to send a payment reminder to ${invoice.customer_name}?`,
      okText: 'Send',
      cancelText: 'Cancel',
      onOk: () => {
        let phoneNumber = invoice.phone_number ? invoice.phone_number.replace(/\D/g, '') : '';
        if (phoneNumber.startsWith('91')) phoneNumber = phoneNumber.slice(2);
        phoneNumber = `91${phoneNumber || '9825514255'}`;

        const totalAmount = parseFloat(invoice.total_amount || 0);
        const advance = parseFloat(invoice.advance || 0);
        const remainingAmount = (totalAmount - advance).toFixed(2);

        const message = `Dear ${invoice.customer_name},

This is a reminder regarding your pending payment with Yogi Graphics.

Invoice No: ${invoice.invoice_number}
Date: ${moment(invoice.created_at).format('DD-MMM-YYYY')}
Total Amount: ₹${totalAmount.toFixed(2)}
Advance Paid: ₹${advance.toFixed(2)}
Remaining Amount: ₹${remainingAmount}

Please complete the remaining payment at your earliest convenience.

-- Yogi Graphics`;

        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
        notification.success({
          message: 'Reminder Sent',
          description: `WhatsApp reminder sent to ${invoice.customer_name}`,
        });
      }
    });
  };
const columns = [
  {
    title: "Line No.",
    dataIndex: "no",
    width: 70,
    align: "center",
    render: (_, __, index) => (
      <Text strong style={{ color: "#1890ff" }}>{index + 1}</Text>
    ),
  },
  {
    title: "Invoice No",
    dataIndex: "invoice_number",
    key: "invoice_number",
    width: 120,
    align: "center",
    render: (text) => <Text strong>{text}</Text>,
  },
  {
    title: "Product Details",
    dataIndex: "items",
    key: "items",
    width: 200,
    render: (items) => (
      <div style={{ lineHeight: "1.4", whiteSpace: "normal" }}>
        {items && items.length > 0 ? (
          items.map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "2px 0",
                borderBottom:
                  i < items.length - 1 ? "1px dashed #eee" : "none",
              }}
            >
              <Text style={{ fontSize: 12, flex: 1 }}>{item.product_name}</Text>
            </div>
          ))
        ) : (
          <Text type="secondary">No Products</Text>
        )}
      </div>
    ),
  },
  {
    title: "QTY",
    dataIndex: "items",
    key: "quantity",
    width: 80,
    align: "center",
    render: (items) => (
      <div>
        {items && items.length > 0 ? (
          items.map((item, i) => (
            <div key={i} style={{ padding: "2px 0" }}>
              <Text style={{ fontSize: 12 }}>{item.quantity}</Text>
            </div>
          ))
        ) : (
          <Text type="secondary">-</Text>
        )}
      </div>
    ),
  },
  {
    title: "Rate (₹)",
    dataIndex: "items",
    key: "rate",
    width: 100,
    align: "right",
    render: (items) => (
      <div>
        {items && items.length > 0 ? (
          items.map((item, i) => (
            <div key={i} style={{ padding: "2px 0" }}>
              <Text style={{ fontSize: 12 }}>₹{parseFloat(item.price || 0).toFixed(2)}</Text>
            </div>
          ))
        ) : (
          <Text type="secondary">-</Text>
        )}
      </div>
    ),
  },
  {
    title: "Amount (₹)",
    dataIndex: "total_amount",
    key: "total_amount",
    width: 110,
    align: "right",
    render: (amt) => (
      <Text strong style={{ color: "#1890ff" }}>
        ₹{parseFloat(amt || 0).toFixed(2)}
      </Text>
    ),
  },
  {
    title: "Advance (₹)",
    dataIndex: "advance",
    key: "advance",
    width: 110,
    align: "right",
    render: (adv) => (
      <Text style={{ color: "#52c41a" }}>
        ₹{parseFloat(adv || 0).toFixed(2)}
      </Text>
    ),
  },
  {
    title: "Remaining (₹)",
    key: "paid_amount",
    width: 120,
    align: "right",
    render: (_, record) => {
      const total = parseFloat(record.total_amount || 0);
      const adv = parseFloat(record.advance || 0);
      const remaining = total - adv;
      return (
        <Text
          strong
          style={{
            color: remaining > 0 ? "#fa541c" : "#52c41a",
          }}
        >
          ₹{remaining.toFixed(2)}
        </Text>
      );
    },
  },
  {
    title: "Customer Name",
    dataIndex: "customer_name",
    key: "customer_name",
    width: 160,
    render: (name, record) => (
      <div>
        <Text strong style={{ display: "block" }}>{name}</Text>
        {record.phone_number && (
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.phone_number}
          </Text>
        )}
      </div>
    ),
  },
  {
    title: "Actions",
    key: "action",
    width: 170,
    fixed: "right",
    align: "center",
    render: (text, record) => (
      <Space size="small" direction="vertical" style={{ width: "100%" }}>
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => editInvoice(record)}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={<WhatsAppOutlined />}
            style={{ background: "#25D366", color: "white", border: "none" }}
            onClick={() => sendTotalRemainingReminder(record)}
          >
            WhatsApp
          </Button>
        </Space>
        <Popconfirm
          title="Delete this invoice?"
          onConfirm={() => deleteInvoice(record.id)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            style={{ width: "100%" }}
          >
            Delete
          </Button>
        </Popconfirm>
      </Space>
    ),
  },
];


  const editInvoice = (invoice) => {
    setEditingInvoice(invoice);

    form.setFieldsValue({
      invoiceNumber: invoice.invoice_number,
      customer: invoice.customer_name,
      address: invoice.address || "",
      number: invoice.phone_number || "",
      invoiceDate: moment(invoice.created_at),
      discount: invoice.discount || 0,
      advance: invoice.advance || 0,
      paid_amount: invoice.paid_amount || 0,
      total_amount: invoice.total_amount || 0,
      products: invoice.items
        ? invoice.items.map((item) => ({
            key: item.id,
            product: item.product_name,
            quantity: item.quantity,
            price: item.price,
          }))
        : [],
      narration: invoice.narration || "",
    });
  };

  const handleUpdate = async (values) => {
    try {
      const response = await fetch(
        "https://yogigraphics.in/backend/update_invoice.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            invoiceId: editingInvoice.id,
            invoiceNumber: values.invoiceNumber,
            customer: values.customer,
            address: values.address,
            number: values.number,
            invoiceDate: values.invoiceDate.format("YYYY-MM-DD"),
            discount: values.discount,
            advance: values.advance,
            paid_amount: values.paid_amount,
            products: values.products || [],
            narration: values.narration,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        notification.success({
          message: "Success",
          description: "Invoice updated successfully",
        });
        setEditingInvoice(null);
        form.resetFields();
        fetchInvoices();
      } else {
        notification.error({ message: "Error", description: result.message });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update invoice",
      });
    }
  };

  const deleteInvoice = async (id) => {
    try {
      const response = await fetch(
        "https://yogigraphics.in/backend/delete_invoice.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        }
      );

      const result = await response.json();

      if (result.success) {
        notification.success({
          message: "Success",
          description: "Invoice deleted successfully",
        });
        fetchInvoices();
      } else {
        notification.error({ message: "Error", description: result.message });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to delete invoice",
      });
    }
  };

  const downloadCSV = () => {
    const csvData = filteredInvoices.map((invoice) => ({
      "Sr.No.": invoice.invoice_number,
      "Invoice Number": invoice.invoice_number,
      "Customer": invoice.customer_name,
      "Date": moment(invoice.created_at).format("YYYY-MM-DD"),
      "Products": invoice.items
        ? invoice.items
            .map((item) => `${item.product_name} (Qty: ${item.quantity}, Rate: ₹${item.price})`)
            .join("; ")
        : "",
      "Total Amount": `₹${parseFloat(invoice.total_amount || 0).toFixed(2)}`,
      "Advance Payment": `₹${parseFloat(invoice.advance || 0).toFixed(2)}`,
      "Remaining Payment": `₹${(
        parseFloat(invoice.total_amount || 0) - parseFloat(invoice.advance || 0)
      ).toFixed(2)}`,
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "invoices.csv");
  };

  const printInvoice = () => {
    if (!editingInvoice) return;

    const discount = parseFloat(editingInvoice.discount) || 0;
    const advance = parseFloat(editingInvoice.advance) || 0;
    const totalAmount = parseFloat(editingInvoice.total_amount) || 0;
    const remainingAmount = parseFloat(editingInvoice.paid_amount) || 0;
    const discountedAmount = discount > 0 ? 
      totalAmount - totalAmount * (discount / 100) : 
      totalAmount;

    const invoiceHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
      <meta charset="UTF-8" />
      <title>Invoice</title>
      <style>
        @page { size: A5 portrait; margin: 15mm; }
        body { font-family: 'Arial', sans-serif; color: #333; }
        .container { width: 100%; max-width: 520px; margin: auto; padding: 10px; border: 1px solid #ccc; background: #fff; position: relative; }
        .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
        .header img { height: 60px; }
        .header h1 { font-size: 20px; margin: 0 auto; text-align: center; flex: 1; }
        .flex-box { display: flex; justify-content: space-between; gap: 10px; margin-top: 10px; }
        .box { width: 49%; border: 1px solid #ccc; padding: 6px; font-size: 12px; border-radius: 4px; }
        .box p { margin: 4px 0; }
        .items { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
        .items th, .items td { border: 1px solid #ccc; padding: 5px; text-align: left; }
        .items th { background-color: #f0f0f0; }
        .total { font-weight: bold; text-align: right; }
        .amount-summary { margin-top: 10px; font-size: 12px; }
        .amount-row { display: flex; justify-content: space-between; padding: 3px 0; }
        .footer { font-size: 10px; border-top: 1px solid #aaa; margin-top: 10px; padding-top: 5px; }
        .signatory { font-size: 11px; text-align: right; margin-top: 40px; }
        .qr-code { position: absolute; bottom: 20px; left: 20px; height: 80px; }
      </style>
      </head>
      <body>
      <div class="container">
        <div class="header">
          <img src="${logoUrl}" alt="Logo" />
          <h1>INVOICE</h1>
        </div>
        <div class="flex-box">
          <div class="box">
            <p><strong>Seller:</strong> Yogi Graphics</p>
            <p><strong>Address:</strong> 8, Ramnath Mahadev Shopping Center</p>
            <p> Opp. Zakli Talav, Nr. Bus Stand, Borsad 388540</p>
            <p><strong>Phone:</strong> +91 98255 14255</p>
            <p><strong>Email:</strong> yogiborsad@gmail.com</p>
          </div>
          <div class="box">
            <p><strong>Invoice No:</strong> ${editingInvoice.invoice_number}</p>
            <p><strong>Date:</strong> ${moment(editingInvoice.created_at).format("DD-MMM-YYYY")}</p>
            <p><strong>Customer:</strong> ${editingInvoice.customer_name}</p>
            <p><strong>Address:</strong> ${editingInvoice.address || ""}</p>
            <p><strong>Phone:</strong> ${editingInvoice.phone_number || ""}</p>
          </div>
        </div>
        <table class="items">
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Amt</th>
            </tr>
          </thead>
          <tbody>
            ${
              editingInvoice.items?.map((item, index) => {
                const qty = parseFloat(item.quantity || 0);
                const price = parseFloat(item.price || 0);
                const amount = (qty * price).toFixed(2);
                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.product_name}</td>
                    <td>${qty}</td>
                    <td>₹${price.toFixed(2)}</td>
                    <td>₹${amount}</td>
                  </tr>`;
              }).join("") || ""
            }
          </tbody>
        </table>
        <div class="amount-summary">
          ${discount > 0 ? `
            <div class="amount-row">
              <span>Subtotal:</span>
              <span>₹${totalAmount.toFixed(2)}</span>
            </div>
            <div class="amount-row">
              <span>Discount (${discount}%):</span>
              <span>- ₹${(totalAmount * discount / 100).toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="amount-row">
            <span><strong>Total Amount:</strong></span>
            <span><strong>₹${discountedAmount.toFixed(2)}</strong></span>
          </div>
          <div class="amount-row">
            <span>Advance Paid:</span>
            <span>- ₹${advance.toFixed(2)}</span>
          </div>
          <div class="amount-row">
            <span><strong>Remaining Amount:</strong></span>
            <span><strong>₹${remainingAmount.toFixed(2)}</strong></span>
          </div>
        </div>
        <div class="footer">
          <p><strong>Terms & Conditions:</strong> </p>
          <p>(1) All sales are final. Goods once sold cannot be returned, exchanged, or refunded.</p>
          <p>(2) Subject to Borsad jurisdiction.</p>
        </div>
        <br>
        <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
        <div class="signatory">
          <p>for Yogi Graphics</p>
          <p>Authorized Signature</p>
        </div>
      </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(invoiceHtml);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className="fade-in">
      <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  TOTAL SALES VOLUME
                </Typography.Text>
                <Statistic
                  value={totalInvoiceAmount}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#0F172A', fontWeight: 800, fontSize: '22px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#EEF2FF', color: '#4F46E5' }}>
                <FileTextOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  TOTAL ADVANCE PAID
                </Typography.Text>
                <Statistic
                  value={totalAdvance}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#10B981', fontWeight: 800, fontSize: '22px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#ECFDF5', color: '#10B981' }}>
                <DollarOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  TOTAL REMAINING BALANCE
                </Typography.Text>
                <Statistic
                  value={totalRemaining}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#F59E0B', fontWeight: 800, fontSize: '22px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#FFFBEB', color: '#F59E0B' }}>
                <DollarOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  ITEMS SOLD (QTY)
                </Typography.Text>
                <Statistic
                  value={totalStockUsed}
                  valueStyle={{ color: '#9333EA', fontWeight: 800, fontSize: '22px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#F3E8FF', color: '#9333EA' }}>
                <ShoppingOutlined />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card 
        title={
          <Space size="middle">
            <FileTextOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
            <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Invoice Records & Sales History</span>
            <Tag color="indigo" style={{ borderRadius: '12px', fontWeight: 600, padding: '2px 10px' }}>
              {filteredInvoices.length} Total Invoices
            </Tag>
          </Space>
        } 
        style={{ borderRadius: '16px' }}
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }} justify="space-between" align="middle">
          <Col xs={24} sm={16} md={12}>
            <Input.Search
              placeholder="Search by customer name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%' }}
              allowClear
              size="large"
            />
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={downloadCSV}
              size="large"
              style={{ borderRadius: '10px' }}
            >
              Export CSV Report
            </Button>
          </Col>
        </Row>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table 
            columns={columns} 
            dataSource={filteredInvoices} 
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        )}
      </Card>
      <Modal
        title="Edit Invoice"
        visible={!!editingInvoice}
        onCancel={() => setEditingInvoice(null)}
        footer={[
          <Button key="print" type="primary" onClick={printInvoice}>
            Print
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            {editingInvoice ? "Update" : "Create"}
          </Button>,
          <Button key="cancel" onClick={() => setEditingInvoice(null)}>
            Cancel
          </Button>,
        ]}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="invoiceNumber"
                label="Invoice Number"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customer"
                label="Customer"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="number" label="Phone Number">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="invoiceDate"
                label="Date"
                rules={[{ required: true }]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="discount" label="Discount">
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="advance" label="Advance Payment">
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  style={{ width: "100%" }} 
                  onChange={handleAdvanceChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="narration" label="Narration">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="total_amount"
                label="Total Amount"
                rules={[
                  { required: true, message: "Please enter the amount" },
                ]}
              >
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  style={{ width: "100%" }} 
                  onChange={handleTotalAmountChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paid_amount"
                label="Remaining Amount"
              >
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  style={{ width: "100%" }} 
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="products" label="Products">
            <Form.List name="products">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <Row gutter={20} key={key}>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          label={`Product ${key + 1}`}
                          name={[name, "product"]}
                          fieldKey={[fieldKey, "product"]}
                          rules={[ { required: true, message: "Missing product" } ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          label={`Quantity ${key + 1}`}
                          name={[name, "quantity"]}
                          fieldKey={[fieldKey, "quantity"]}
                          rules={[{ required: true, message: "Missing quantity" }]}
                        >
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          label={`Price ${key + 1}`}
                          name={[name, "price"]}
                          fieldKey={[fieldKey, "price"]}
                          rules={[{ required: true, message: "Missing price" }]}
                        >
                          <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Button
                          style={{ width: "100%", marginTop: "30px" }}
                          type="default"
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      style={{ width: "100%" }}
                    >
                      Add Product
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default Home;